// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type LoaderFunction } from 'react-router';
import { trackPromise } from 'react-promise-tracker';
import { RTK_PROMISE_TRACKER_ID } from '@shared/data-access/utils/constants';
import { appStore } from '@advisor-app/store/store';
import { advisoryApi } from '@shared/data-access/api/brokerage/advisory-api.v2';
import { selectClientId } from '@shared/data-access/slices/clients/clients-selectors';
import { selectAccountCurrency } from '@shared/data-access/slices/user';
import { isDefined } from '@shared/utils';
import { isTest } from '@shared/environment';

/**
 * Wait until redux-persist has rehydrated so active client and currency are
 * available.
 */
const waitForRehydration = (): Promise<void> =>
  new Promise(resolve => {
    if (isTest) {
      resolve();
      return;
    }

    const currentState = appStore.getState();
    if (currentState._persist?.rehydrated) {
      resolve();
      return;
    }

    const unsubscribe = appStore.subscribe(() => {
      const state = appStore.getState();
      if (state._persist?.rehydrated) {
        unsubscribe();
        resolve();
      }
    });
  });

/**
 * Loads advisory institutions for the active client into the clients slice
 * (`getAdvisoryInstitutions` → middleware `setInstitutions`).
 */
export const clientInstitutionsLoader: LoaderFunction = async () =>
  trackPromise(
    (async () => {
      await waitForRehydration();

      const state = appStore.getState();
      const clientId = selectClientId(state);

      if (!isDefined(clientId) || clientId === 0) {
        return { ok: true };
      }

      const currency = selectAccountCurrency(state);

      await appStore.dispatch(
        advisoryApi.endpoints.getAdvisoryInstitutions.initiate({
          currency,
          client_id: clientId,
        }),
      );

      return { ok: true };
    })(),
    RTK_PROMISE_TRACKER_ID,
  );
